import { colorConstants } from 'config/colorConstants';
import type {
    SimulationBulkUpdateConfig,
    SimulationTableColumnMeta,
    SimulationTableConfig,
} from 'types/ppaSimulationMeta';
import type {
    GenericScenarioKpiGroupConfig,
    GenericScenarioTableColumnConfig,
    GenericScenarioTableUiConfig,
} from './genericScenarioTable.types';
import {
    EMPTY_KPI_COLUMNS,
    type SimulationKpiKind,
    type SimulationVisibleAttributeColumn,
    type SimulationVisibleKpiColumn,
} from './simulationTable.helpersTypes';
import { columnType } from './simulationTable.helpersVisibility';

function widthForHeaderLabel(title: string, extras: number, minWidth: number): number {
    return Math.max(minWidth, title.length * 8 + extras);
}

function isEditableMetaColumn(column: SimulationTableColumnMeta): boolean {
    if (columnType(column) === 'input_box') return true;
    return column.editMode === 'full';
}

function isSelectColumn(column: SimulationTableColumnMeta, type: string): boolean {
    return column.id === 'select' || (type === 'checkbox' && column.id === 'select');
}

function isAttributeColumn(column: SimulationTableColumnMeta, type: string): boolean {
    return Boolean(column.isAttribute) || type === 'attribute';
}

function isHiddenKpiMetaColumn(column: SimulationTableColumnMeta, type: string): boolean {
    return column.id === 'kpi_id' || (type === 'kpi' && column.id !== 'ai');
}

function isCompetitorCheckboxColumn(column: SimulationTableColumnMeta, type: string): boolean {
    return type === 'checkbox' && column.id !== 'select';
}

export function resolveBulkUpdateMetricKey(fieldId: string, metricKeys: string[]): string {
    const normalized = fieldId.trim().toLowerCase();
    if (metricKeys.includes(fieldId)) return fieldId;
    if (normalized === 'change' && metricKeys.includes('chg')) return 'chg';
    return metricKeys.find((key) => key.toLowerCase() === normalized) ?? fieldId;
}

function buildKpiGroupFromMeta(
    column: SimulationTableColumnMeta,
    valuePrefix: string,
    options: { key?: string; title?: string; bulkUpdate?: SimulationBulkUpdateConfig } = {},
): GenericScenarioKpiGroupConfig {
    return {
        key: options.key ?? column.id,
        title: options.title ?? column.label,
        sortType: 'kpi',
        bulkUpdate: options.bulkUpdate,
        metrics: (column.children ?? []).map((metric) => {
            const isChange = metric.id === 'chg' || metric.suffix === '%';
            const suffix = metric.suffix ?? '';
            const prefix = isChange ? '' : valuePrefix;
            const isEditable = isEditableMetaColumn(metric);

            return {
                key: metric.id,
                title: metric.label,
                cellType: isEditable ? ('editableInput' as const) : ('staticMetric' as const),
                displayClassName: isEditable ? 'text-[12px] leading-[18px]' : 'text-[13px] leading-[18px]',
                displayColor: isEditable ? colorConstants.tableMutedText : colorConstants.tableBodyText,
                displayFormat: {
                    prefix,
                    suffix,
                    emptyDisplay: `${prefix}${metric.placeholder ?? ''}${suffix}`,
                },
                editor: isEditable
                    ? {
                          showTooltip: false,
                          tooltipText: '',
                          allowedValueType: (metric.valueType === 'number' ? 'number' : 'any') as 'number' | 'any',
                          allowDecimal: true,
                          decimalPlaces: 2 as const,
                          prefixText: prefix,
                          suffixText: suffix,
                      }
                    : undefined,
            };
        }),
    };
}

function buildLeafColumnFromMeta(
    column: SimulationTableColumnMeta,
    options: {
        filterSortable?: boolean;
        sortable?: boolean;
        sortType?: GenericScenarioTableColumnConfig['sortType'];
        isDimension?: boolean;
        key?: string;
        title?: string;
    } = {},
): GenericScenarioTableColumnConfig {
    const type = columnType(column);
    const key = options.key ?? column.id;
    const title = options.title?.trim() || column.label;

    if (type === 'date_picker') {
        return {
            key,
            dataIndex: key,
            title,
            width: widthForHeaderLabel(title, 56, 220),
            cellType: 'datePicker',
            emptyDisplay: column.placeholder || 'DD/MM/YY - DD/MM/YY',
            sortable: options.sortable ?? true,
            sortType: options.sortType ?? 'date',
            editor: {
                placeholder: column.placeholder || 'DD/MM/YY - DD/MM/YY',
                startWeekday: 'friday',
                showTooltip: false,
                tooltipText: '',
            },
        };
    }

    if (type === 'checkbox') {
        return {
            key,
            dataIndex: key,
            title,
            width: widthForHeaderLabel(title, 72, 200),
            cellType: 'checkbox',
        };
    }

    return {
        key,
        dataIndex: key,
        title,
        width: options.isDimension ? 220 : 160,
        fixed: options.isDimension ? 'left' : undefined,
        cellType: 'text',
        emptyDisplay: column.placeholder || ' ',
        isDimension: options.isDimension,
        filterSortable: options.filterSortable,
        sortable: options.sortable,
        sortType: options.sortType ?? (options.filterSortable || options.sortable ? 'text' : undefined),
        showTitleTooltip: Boolean(title),
        titleTooltipText: title,
    };
}

function buildAttributeColumns(
    column: SimulationTableColumnMeta,
    visibleAttributeColumns: SimulationVisibleAttributeColumn[],
): GenericScenarioTableColumnConfig[] {
    if (visibleAttributeColumns.length === 0) {
        return [buildLeafColumnFromMeta(column, { isDimension: true, filterSortable: true, sortType: 'text' })];
    }

    return visibleAttributeColumns.map((visibleColumn) =>
        buildLeafColumnFromMeta(column, {
            isDimension: true,
            filterSortable: true,
            sortType: 'text',
            key: visibleColumn.attributeKey,
            title: visibleColumn.displayName,
        }),
    );
}

function buildInputKpiGroups(
    column: SimulationTableColumnMeta,
    inputKpis: SimulationVisibleKpiColumn[],
    valuePrefix: string,
    bulkUpdate?: SimulationBulkUpdateConfig,
): GenericScenarioKpiGroupConfig[] {
    if (inputKpis.length === 0) {
        return [buildKpiGroupFromMeta(column, valuePrefix, { bulkUpdate })];
    }

    return inputKpis.map((kpi) =>
        buildKpiGroupFromMeta(column, kpi.valuePrefix || valuePrefix, {
            key: kpi.kpiId,
            title: kpi.displayName || column.label,
            bulkUpdate,
        }),
    );
}

function buildDateKpiColumns(
    column: SimulationTableColumnMeta,
    dateKpis: SimulationVisibleKpiColumn[],
): GenericScenarioTableColumnConfig[] {
    if (dateKpis.length === 0) {
        return [buildLeafColumnFromMeta(column, { sortable: true, sortType: 'date' })];
    }

    return dateKpis.map((kpi) =>
        buildLeafColumnFromMeta(column, {
            key: kpi.kpiId,
            title: column.label,
            sortable: true,
            sortType: 'date',
        }),
    );
}

function buildCheckboxKpiColumns(
    column: SimulationTableColumnMeta,
    checkboxKpis: SimulationVisibleKpiColumn[],
): GenericScenarioTableColumnConfig[] {
    if (checkboxKpis.length === 0) {
        return [buildLeafColumnFromMeta(column)];
    }

    return checkboxKpis.map((kpi) =>
        buildLeafColumnFromMeta(column, {
            key: kpi.kpiId,
            title: column.label,
        }),
    );
}

export function buildSimulationTableUiConfig({
    tableConfig,
    nestDimensions,
    recordLabel,
    valuePrefix = '',
    visibleAttributeColumns = [],
    visibleKpiColumns = EMPTY_KPI_COLUMNS,
}: {
    tableConfig: SimulationTableConfig;
    nestDimensions: boolean;
    recordLabel: string;
    valuePrefix?: string;
    visibleAttributeColumns?: SimulationVisibleAttributeColumn[];
    visibleKpiColumns?: Record<SimulationKpiKind, SimulationVisibleKpiColumn[]>;
}): GenericScenarioTableUiConfig {
    let showRowSelection = false;
    let showAiColumn = false;
    let aiLabel = 'AI';
    let aiColumnId = 'ai';
    const standardColumns: GenericScenarioTableColumnConfig[] = [];
    const kpiGroups: GenericScenarioKpiGroupConfig[] = [];
    const defaultKpiColumns: GenericScenarioTableColumnConfig[] = [];

    tableConfig.columns.forEach((column) => {
        const type = columnType(column);
        if (isSelectColumn(column, type)) {
            showRowSelection = true;
            return;
        }
        if (column.id === 'ai') {
            showAiColumn = true;
            aiLabel = column.label;
            aiColumnId = column.id;
            return;
        }
        if (isAttributeColumn(column, type)) {
            standardColumns.push(...buildAttributeColumns(column, visibleAttributeColumns));
            return;
        }
        if (isHiddenKpiMetaColumn(column, type)) return;
        if (type === 'table_columns') {
            kpiGroups.push(
                ...buildInputKpiGroups(column, visibleKpiColumns.input, valuePrefix, tableConfig.bulkUpdate),
            );
            return;
        }
        if (type === 'date_picker') {
            defaultKpiColumns.push(...buildDateKpiColumns(column, visibleKpiColumns.date));
            return;
        }
        if (isCompetitorCheckboxColumn(column, type)) {
            defaultKpiColumns.push(...buildCheckboxKpiColumns(column, visibleKpiColumns.checkbox));
            return;
        }
        standardColumns.push(buildLeafColumnFromMeta(column));
    });

    return {
        ids: {
            container: 'generic-scenario-table-container',
        },
        behavior: {
            editOnHover: true,
            editOnClick: true,
            showSaveActions: true,
            isFlattened: !nestDimensions,
            showRowSelection,
            showAiColumn,
            showAddRow: false,
        },
        table: {
            heightBuffer: 160,
            minBodyHeight: 220,
            scrollX: 'max-content',
            size: 'small',
            rowSelectionWidth: 44,
            repeatedValueOpacity: 0.3,
        },
        actionButton: {
            ariaLabel: 'Add row',
        },
        footer: {
            recordLabel,
            statusLabel: '',
            refreshLabel: '',
        },
        aiColumn: {
            key: aiColumnId,
            dataIndex: aiColumnId,
            title: aiLabel,
            width: 90,
            fixed: 'left',
            showTitleTooltip: true,
            expandAriaLabel: {
                collapsed: 'Expand row',
                expanded: 'Collapse row',
            },
            icon: {
                backgroundColor: colorConstants.breadcrumbSlash,
                lineColor: colorConstants.white,
            },
            parentHintClassName: 'mt-[2px] text-[10px]',
            parentHintColor: colorConstants.tableHintText,
            allDimensionValue: 'All',
            expandableLabels: [],
        },
        standardColumns,
        kpiGroups,
        defaultKpiColumns,
        columnFilterSort: tableConfig.columnFilterSort,
    };
}
